import React from 'react';
import {Box, Typography} from '@mui/material';

export interface Range {
  minVal: number;
  maxVal: number;
  label: string;
}

interface Props {
  title: string;
  min: number;
  max: number;
  value: number;
  ranges: Range[];
}

export const Slider: React.FC<Props> = ({
                                          title,
                                          min,
                                          max,
                                          value,

                                          ranges,
                                        }) => {

  // Spacing for the outer padding (left/right) in percent
  const horizontalPaddingPercent = 6;

  const paddedPercent = (val: number) => {
    const range = max - min;
    const usablePercent = 100 - horizontalPaddingPercent * 2;
    const clampedVal = Math.max(min, Math.min(max, val)); // Clamp val between min and max
    return horizontalPaddingPercent + (clampedVal - min) / range * usablePercent;
  };

  return (
      <Box my={5}>

        {/* Title */}
        <Box display="flex" justifyContent="center" alignItems="center" mb={3}>
          <Typography variant="h3">
            {title}
          </Typography>
        </Box>

        <Box position="relative" width="100%" height={90}>

          {/* Marker values */}
          {ranges.map((range, idx) => {
            if (idx !== ranges.length - 1) {
              return (
                  <Box
                      key={`label-${idx}`}
                      sx={{
                        position: 'absolute',
                        left: `${paddedPercent(range.maxVal)}%`,
                        transform: 'translateX(-50%)',
                        textAlign: 'center',
                        top: 18,
                      }}
                  >
                    <Typography
                        variant="h4"
                        sx={{
                          lineHeight: 0,
                          fontSize: '1.3rem'
                    }}
                    >
                      {`${range.maxVal}%`}
                    </Typography>
                  </Box>
              );
            }
          })}

          {/* Marker labels */}
          {ranges.map((range, idx) => {
            return (
                <Box
                    key={`label-${idx}`}
                    sx={{
                      position: 'absolute',
                      left: idx === ranges.length-1 ? `${paddedPercent(
                          ((max - range.minVal) / 2) +
                          range.minVal)}%` : `${paddedPercent(
                          ((range.maxVal - range.minVal) / 2) +
                          range.minVal)}%` ,
                      transform: 'translateX(-50%)',
                      textAlign: 'center',
                      top: idx % 2 === 0 ? -22 : 75, // Alternate every other label
                    }}
                >
                  <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{lineHeight: 3}}
                  >
                    {range.label}
                  </Typography>
                </Box>
            );
          })}

          {/* Track */}
          <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: `${horizontalPaddingPercent}%`,
                right: `${horizontalPaddingPercent}%`,
                height: 16,
                borderRadius: 8,
                backgroundColor: '#e0e0e0',
                transform: 'translateY(-50%)',
              }}
          />

          {/* Tick marks */}
          {ranges.map((marker, idx) => {
            if (idx !== ranges.length - 1) {
              return (
                  <Box
                      key={`tick-${idx}`}
                      sx={{
                        position: 'absolute',
                        top: 'calc(50% - 8px)',
                        left: `${paddedPercent(marker.maxVal)}%`,
                        transform: 'translateX(-50%)',
                        width: '1px',
                        height: '16px',
                        backgroundColor: '#333',
                      }}
                  />
              );
            }
          })}

          {/* Thumb */}
          <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: `${paddedPercent(value)}%`,
                transform: 'translate(-50%, -50%)',
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: '#1976d2',
              }}
          />

          {/* Value under thumb */}
          <Box
              sx={{
                position: 'absolute',
                top: '70%',
                left: `${paddedPercent(value)}%`,
                transform: 'translateX(-50%)',
              }}
          >
            <Typography variant="h4">
              {`${value}%`}
            </Typography>
          </Box>
        </Box>
      </Box>
  );
};